/**
 * Client-side password hashing utility
 * Hashes passwords before sending to server for enhanced security
 */

// Simple client-side password hashing using Web Crypto API
export const hashPassword = async (password: string): Promise<string> => {
  try {
    // Convert password to bytes
    const encoder = new TextEncoder();
    const data = encoder.encode(password);

    // Hash the password using SHA-256
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert hash to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  } catch (error) {
    console.error('Password hashing failed:', error);
    // Fallback: return original password if hashing fails
    // In production, you might want to throw an error instead

    return password;
  }
};

// Validate password strength on client side (matches backend validation)
export const validatePasswordStrength = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  let score = 0;

  // Length scoring (matches backend)
  if (password.length >= 16) score += 3;
  else if (password.length >= 12) score += 2;
  else if (password.length >= 8) score += 1;
  else errors.push('Use at least 8 characters (12+ recommended)');

  // Character variety scoring (matches backend)
  if (/[a-z]/.test(password)) score += 1;
  else errors.push('Include lowercase letters');

  if (/[A-Z]/.test(password)) score += 1;
  else errors.push('Include uppercase letters');

  if (/[0-9]/.test(password)) score += 1;
  else errors.push('Include numbers');

  // eslint-disable-next-line no-useless-escape
  if (/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) score += 2;
  else errors.push('Include special characters');

  // Calculate entropy (simplified version)
  const charsetSize = getCharsetSize(password);
  const entropy = Math.log2(Math.pow(charsetSize, password.length));

  // Entropy bonus
  if (entropy >= 80) score += 3;
  else if (entropy >= 60) score += 2;
  else if (entropy >= 40) score += 1;

  // Pattern penalties
  if (/(.)\1{2,}/.test(password)) {
    score -= 1;
    errors.push('Avoid repeating characters');
  }

  if (/123|abc|qwe|password|admin|user/i.test(password)) {
    score -= 2;
    errors.push('Avoid common patterns and dictionary words');
  }

  // Minimum requirements: score >= 5 AND entropy >= 40 (matches backend)
  const isSecure = score >= 5 && entropy >= 40;

  return {
    isValid: isSecure,
    errors: errors.length > 0 ? errors : isSecure ? [] : ['Password does not meet security requirements'],
  };
};

// Helper function to calculate charset size
const getCharsetSize = (password: string): number => {
  let size = 0;

  if (/[a-z]/.test(password)) size += 26;
  if (/[A-Z]/.test(password)) size += 26;
  if (/[0-9]/.test(password)) size += 10;
  // eslint-disable-next-line no-useless-escape
  if (/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) size += 32;

  return size;
};

// Helper to check if browser supports crypto API
export const isCryptoSupported = (): boolean =>
  typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined' && typeof crypto.subtle.digest === 'function';
